import React, { useState, useEffect, useRef } from 'react';
import type { Participant } from '../types';

// Official Patch My PC UI/UX Design Kit colors for wheel segments
const SEGMENT_COLORS = [
  '#1BBC9B', // Sea-Foam Green 500 (Primary)
  '#0490DA', // PMPC Blue 500 (Secondary)
  '#50CD89', // Mint Accent (Success)
  '#FFC700', // Golden Accent (Warning)
  '#A091FE', // Lavender Accent
  '#71B2FE', // Periwinkle Accent
  '#11A486', // Sea-Foam Green 600
  '#5398FF', // Sky Accent
];

// Maximum participants to show on wheel before switching to simplified view
const MAX_WHEEL_PARTICIPANTS = 50;

const DEFAULT_WHEEL_SIZE = 400;
const MIN_WHEEL_SIZE = 200;
// Padding to account for the pointer element above the canvas
const CONTAINER_PADDING = 40;

interface PrizeWheelProps {
  participants: Participant[];
  spinning: boolean;
  winner?: Participant | null; // The predetermined winner from server
  onSpinComplete: () => void;
  maxSize?: number; // Optional cap on wheel diameter
}

function PrizeWheel({ participants, spinning, winner, onSpinComplete, maxSize }: PrizeWheelProps) {
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [wheelSize, setWheelSize] = useState(DEFAULT_WHEEL_SIZE);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // PERFORMANCE: If too many participants, use a simplified wheel with just the winner highlighted
  const tooManyParticipants = participants.length > MAX_WHEEL_PARTICIPANTS;
  const displayParticipants = tooManyParticipants
    ? participants.slice(0, MAX_WHEEL_PARTICIPANTS) // Show first 50 only on wheel
    : participants;

  // Measure container and update wheel size via ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      // Don't resize during spin animation
      if (isSpinning) return;

      const rect = container.getBoundingClientRect();
      const available = Math.floor(Math.min(rect.width, rect.height)) - CONTAINER_PADDING;
      const clamped = Math.max(MIN_WHEEL_SIZE, maxSize ? Math.min(available, maxSize) : available);
      setWheelSize(clamped);
    };

    // Initial measurement
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [maxSize, isSpinning]);

  // Draw the wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = wheelSize / 2;
    const centerY = wheelSize / 2;
    const radius = wheelSize / 2 - 10;

    // Scaled drawing values
    const emptyFontSize = Math.max(12, Math.round(wheelSize * 0.045));
    const nameFontSize = Math.max(10, Math.round(wheelSize * 0.035));
    const centerCircleRadius = Math.max(15, Math.round(wheelSize * 0.075));
    const outerLineWidth = Math.max(2, Math.round(wheelSize * 0.0125));
    const segmentLineWidth = Math.max(1, Math.round(wheelSize * 0.005));
    const centerLineWidth = Math.max(2, Math.round(wheelSize * 0.01));

    // Clear canvas
    ctx.clearRect(0, 0, wheelSize, wheelSize);

    if (participants.length === 0) {
      // Draw empty wheel with PMPC colors
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#2A2A3C'; // Base 700
      ctx.fill();
      ctx.strokeStyle = '#ABADCF'; // Base 100
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.fillStyle = '#FFFFFF'; // Neutral White
      ctx.font = `${emptyFontSize}px Poppins, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No participants', centerX, centerY);
      return;
    }

    // PERFORMANCE: Use displayParticipants (limited to 50) for rendering
    const segmentAngle = (2 * Math.PI) / displayParticipants.length;

    // Save context and rotate
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    // Draw segments - PERFORMANCE: Only draw displayParticipants
    displayParticipants.forEach((participant, index) => {
      const startAngle = index * segmentAngle - Math.PI / 2;
      const endAngle = startAngle + segmentAngle;
      const color = SEGMENT_COLORS[index % SEGMENT_COLORS.length];

      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#151521'; // Base 900
      ctx.lineWidth = segmentLineWidth;
      ctx.stroke();

      // Draw text - skip if too many segments (unreadable anyway)
      if (displayParticipants.length <= 30) {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(startAngle + segmentAngle / 2);
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#151521'; // Base 900
        ctx.font = `bold ${nameFontSize}px Poppins, sans-serif`;

        // Truncate name if too long
        const name = `${participant.first_name} ${participant.last_name.charAt(0)}.`;
        const displayName = name.length > 15 ? name.substring(0, 12) + '...' : name;
        ctx.fillText(displayName, radius - 20, 0);
        ctx.restore();
      }
    });

    ctx.restore();

    // Draw center circle with PMPC colors
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerCircleRadius, 0, 2 * Math.PI);
    ctx.fillStyle = '#151521'; // Base 900
    ctx.fill();
    ctx.strokeStyle = '#1BBC9B'; // Sea-Foam Green 500
    ctx.lineWidth = centerLineWidth;
    ctx.stroke();

    // Draw outer ring with PMPC colors
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#1BBC9B'; // Sea-Foam Green 500
    ctx.lineWidth = outerLineWidth;
    ctx.stroke();
  }, [displayParticipants, rotation, participants.length, wheelSize]);

  // Handle spinning animation - spins to the predetermined winner
  // IMPORTANT: All clients must calculate the same final position for synchronization
  useEffect(() => {
    if (spinning && !isSpinning && displayParticipants.length > 0 && winner) {
      setIsSpinning(true);

      // Find the winner's index in the displayParticipants array
      // For large lists, winner might not be in display list, so we handle that
      let winnerIndex = displayParticipants.findIndex(p => p.id === winner.id);

      // If winner not in display list (too many participants), add them temporarily
      if (winnerIndex === -1 && tooManyParticipants) {
        // Just spin to a random position - the winner reveal will show the actual winner
        winnerIndex = Math.floor(displayParticipants.length / 2);
      } else if (winnerIndex === -1) {
        setIsSpinning(false);
        onSpinComplete();
        return;
      }

      const segmentAngle = 360 / displayParticipants.length;

      // Calculate target rotation to land on the winner
      // The pointer is at the top (12 o'clock position)
      // FIXED: Use deterministic values - no Math.random(), always start from 0
      const targetSegmentCenter = winnerIndex * segmentAngle + segmentAngle / 2;
      const extraSpins = 10; // Fixed number of spins for consistency across all clients
      const finalRotation = 360 * extraSpins + (360 - targetSegmentCenter);

      // Always start from 0 for consistency across all viewers
      const startRotation = 0;
      setRotation(0);

      const duration = 12000; // 12 seconds (between 10-20 second requirement)
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease out cubic)
        const eased = 1 - Math.pow(1 - progress, 3);

        const currentRotation = startRotation + finalRotation * eased;
        setRotation(currentRotation % 360);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Ensure we end exactly on the target
          setRotation((360 - targetSegmentCenter) % 360);
          setIsSpinning(false);
          onSpinComplete();
        }
      };

      requestAnimationFrame(animate);
    }
  }, [spinning, isSpinning, displayParticipants, winner, onSpinComplete, tooManyParticipants]);

  return (
    <div className="wheel-wrapper" ref={containerRef}>
      <div className="wheel-pointer">&#9660;</div>
      {tooManyParticipants && (
        <div className="wheel-notice">
          {participants.length} participants (showing sample on wheel)
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={wheelSize}
        height={wheelSize}
        style={{
          borderRadius: '50%',
          boxShadow: '0 10px 40px rgba(27, 188, 155, 0.2)', // Sea-Foam Green glow
        }}
      />
    </div>
  );
}

export default PrizeWheel;
